import { AfterViewInit, Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UUID } from '@finxone-platform/shared/base-types';
import {
  FormsConfigType,
  ProjectFormsConfig,
  WorkflowWidgetConfig,
} from '@finxone-platform/shared/sys-config-types';
import { formWidgetConfig } from '@finxone-platform/shared/workflow-mapping';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Subject, takeUntil } from 'rxjs';
import { DisplayPopupAction } from '../../../libs/actions/app-grid.action';
import { WorkflowEditorService } from '../../../libs/services/workflow-editor/workflow-editor.service';
import {
  getAllFormIdsFromEnum,
  getFormIdKeyValuePairs,
} from '../../../libs/services/workflow-form-editor/form-config.utils';
import { WorkflowFormEditorService } from '../../../libs/services/workflow-form-editor/workflow-form-editor.service';
import { AutoDestroy } from '../../../libs/utils/utils';

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-form-widget-popup',
  templateUrl: './form-widget-popup.component.html',
  styleUrls: ['./form-widget-popup.component.scss'],
})
export class FormWidgetPopupComponent implements AfterViewInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public isFormLinkedToExistingForm = false;
  private formsList: string[] = [];
  private projectForms: ProjectFormsConfig[] = [];
  private formWidgetConfig: WorkflowWidgetConfig = {} as WorkflowWidgetConfig;
  public formGroup = new FormGroup({
    formId: new FormControl<string>('', [Validators.required]),
  });
  public filteredFormsList: string[] = [];
  private formDescriptionMap: Map<string, string> = new Map();
  public selectedFormDescription: string | null = null;

  constructor(
    private readonly store: Store,
    private readonly ngxSmartModalService: NgxSmartModalService,
    private readonly workflowEditorService: WorkflowEditorService,
    private readonly workflowFormEditorService: WorkflowFormEditorService,
  ) {}

  ngAfterViewInit(): void {
    this.refreshDataOnPopupOpen();
    this.clearDataOnPopupClose();
  }

  public onKeydown(event: KeyboardEvent): void {
    const key = event.key;
    // Clear description when user types a custom form ID (letters/numbers), presses Enter or Backspace
    if (key === 'Enter' || key === 'Backspace' || /^[a-z0-9]$/i.test(key)) {
      this.clearFormIdDescription();
      return;
    }

    // preserve previous behavior: prevent default for Space and Tab
    if (event.code === 'Space' || event.code === 'Tab') {
      event.preventDefault();
    }
  }

  private refreshDataOnPopupOpen() {
    const popup = this.ngxSmartModalService.get('formWidgetPopup');
    popup?.onOpen.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // deep clone the widget config to avoid mutation of the original config
      this.formWidgetConfig = cloneDeep(formWidgetConfig);

      // get project forms and system-backed form IDs
      this.projectForms = this.workflowFormEditorService.getProjectForms();
      const projectIds = this.projectForms.map((v) => v.id ?? '');
      const systemFormIds = getAllFormIdsFromEnum() ?? [];

      // combine with project IDs first, then any system IDs not already present
      const projectSet = new Set(projectIds.filter(Boolean));
      this.formsList = [...projectIds, ...systemFormIds.filter((id) => id && !projectSet.has(id))];
      // clone to avoid accidental shared reference and ensure array exists for template
      this.filteredFormsList = [...this.formsList];

      // Build description lookup from service key/value pairs
      const kv = getFormIdKeyValuePairs();
      this.formDescriptionMap = new Map(kv.map((item) => [item.value, item.description ?? '']));

      // If control already has a value, set its description
      const initial = this.formGroup.get('formId')?.value;
      this.selectedFormDescription = initial ? this.formDescriptionMap.get(initial) ?? null : null;
    });
  }

  private clearDataOnPopupClose() {
    const popup = this.ngxSmartModalService.get('formWidgetPopup');
    popup?.onClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.projectForms = [];
      this.formsList = [];
      this.filteredFormsList = [];
      this.formDescriptionMap.clear();
      this.selectedFormDescription = null;
      this.workflowEditorService.clearPendingWidgetDropContext();
      this.store.dispatch(new DisplayPopupAction(null));
      this.formGroup.reset();
      this.formGroup.markAsUntouched();
      this.isFormLinkedToExistingForm = false;
      this.formWidgetConfig = {} as WorkflowWidgetConfig;
    });
  }

  public onSubmit() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const widgetAttri = this.formWidgetConfig.widgetConfiguration.attributes;
    widgetAttri.widgetProperties.formsConfig ??= {} as FormsConfigType;

    widgetAttri.widgetProperties.formsConfig.formID = this.formGroup.get('formId')?.value ?? '';

    const formConfig: ProjectFormsConfig = {
      id: widgetAttri.widgetProperties.formsConfig.formID,
      fieldsConfig: [],
    };
    // remove popupType from widgetConfig to avoid calling the popup again
    delete this.formWidgetConfig.popupType;
    // update uniqueId before adding widget to page
    this.formWidgetConfig.uniqueId = new UUID().toString();
    const placementContext = this.workflowEditorService.consumePendingWidgetDropContext() ?? undefined;
    this.workflowFormEditorService.addFormWidgetAndNewFormConfigInProject(
      formConfig,
      this.formWidgetConfig,
      placementContext,
    );
    this.ngxSmartModalService.get('formWidgetPopup')?.close();
  }

  public cancel() {
    this.ngxSmartModalService.get('formWidgetPopup')?.close();
  }

  public getFilteredForms(event: any) {
    const query = (event?.query ?? '').toString().toLowerCase();
    this.filteredFormsList = this.formsList.filter((v) => v.toLowerCase().includes(query));
  }

  public setSelectedForm(event: any) {
    const value = typeof event === 'string' ? event : event?.value;
    this.formGroup.get('formId')?.setValue(value);
    this.selectedFormDescription = '';
    this.selectedFormDescription = this.formDescriptionMap.get(value) ?? null;
  }

  public clearFormIdDescription() {
    this.selectedFormDescription = '';
  }
}
